import { useCallback, useEffect, useRef, useState } from "react";
import { recordSlideEvent } from "@/lib/api";

/**
 * Slide navigation hook — owns the current index, direction, keyboard handler,
 * and emits analytics events when the slide changes / page unloads.
 *
 * Caller passes the total slide count and the prospect (or null) so we know
 * whether to report engagement seconds.
 */
export function useDeckNavigation({ total, prospect, onRequestOverview, onToggleFullscreen }) {
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState("fwd");
  const slideEnterTs = useRef(Date.now());
  const interactionLockUntil = useRef(0);

  // Exposed so an interactive slide can briefly suppress arrow-key navigation
  // (e.g. while a drag-and-drop is in flight).
  useEffect(() => {
    window.__deckInteractionLock = () => {
      interactionLockUntil.current = Date.now() + 1500;
    };
    return () => { delete window.__deckInteractionLock; };
  }, []);

  const flushSlideTime = useCallback((slideIdx) => {
    const elapsed = Math.max(0, Math.round((Date.now() - slideEnterTs.current) / 1000));
    if (elapsed >= 1 && prospect) recordSlideEvent(slideIdx, elapsed);
  }, [prospect]);

  const go = useCallback((next) => {
    setIdx((cur) => {
      const clamped = Math.max(0, Math.min(total - 1, next));
      if (clamped !== cur) {
        flushSlideTime(cur);
        slideEnterTs.current = Date.now();
      }
      setDirection(clamped >= cur ? "fwd" : "back");
      return clamped;
    });
  }, [total, flushSlideTime]);

  // Flush on tab-hide / page-unload / unmount.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushSlideTime(idx); };
    const onUnload = () => flushSlideTime(idx);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
      flushSlideTime(idx);
    };
  }, [idx, flushSlideTime]);

  // Keyboard navigation. Skips when focus is in inputs / sliders / overview is open.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.target?.getAttribute?.("role") === "slider") return;
      if (Date.now() < interactionLockUntil.current) return;

      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault(); go(idx + 1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault(); go(idx - 1);
      } else if (e.key === "Home") {
        e.preventDefault(); go(0);
      } else if (e.key === "End") {
        e.preventDefault(); go(total - 1);
      } else if (e.key === "Escape") {
        onRequestOverview?.();
      } else if (e.key === "f" || e.key === "F") {
        onToggleFullscreen?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, idx, total, onRequestOverview, onToggleFullscreen]);

  return { idx, direction, go };
}
